import React, { useState } from 'react';
import {
  View, Text, StyleSheet, Pressable, TouchableOpacity, Platform,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import HaulbacksTab from './HaulbacksTab';
import RouteRoadblocksTab from './RouteRoadblocksTab';
import { t } from './i18n';
import { ListLanguageToggle } from '../PerfectTruckManagement/EquipmentConfig';
import { EquipmentLanguage } from '../PerfectTruckManagement/types';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';

// ─── Types ────────────────────────────────────────────────────────────────────

type RpmConfigTab = 'haulbacks' | 'route-roadblocks';

const TABS: { id: RpmConfigTab; label: string }[] = [
  { id: 'haulbacks', label: 'haulbacks' },
  { id: 'route-roadblocks', label: 'route-roadblocks' },
];

interface DeliveryRPMManagementScreenProps {
  onBack: () => void;
}

// ─── Icons ────────────────────────────────────────────────────────────────────


// ─── Main component ───────────────────────────────────────────────────────────

const DeliveryRPMManagementScreen: React.FC<DeliveryRPMManagementScreenProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<RpmConfigTab>('haulbacks');
  const [listLanguage, setListLanguage] = useState<EquipmentLanguage>('en');

  return (
    <View style={s.root}>
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <View style={s.header}>
        <View style={s.headerTop}>
          <View style={s.headerLeft}>
            <View style={s.crumbs}>
              <TouchableOpacity style={s.backBtn} onPress={onBack}>
                <Text style={s.backText}>{t(listLanguage, 'breadcrumb.configurations')}</Text>
              </TouchableOpacity>
              <Text style={s.crumbSep}>{'>'}</Text>
              <Text style={s.crumbCur}>{t(listLanguage, 'breadcrumb.deliveryRpm')}</Text>
            </View>
            <View style={s.pageHead}>
              <Text style={s.pageTitle}>{t(listLanguage, 'pageTitle')}</Text>
            </View>
          </View>
          <View style={s.headerRight}>
            <ListLanguageToggle value={listLanguage} onChange={setListLanguage} />
          </View>
        </View>
      </View>

      {/* ── Tab Bar ─────────────────────────────────────────────────── */}
      <View style={s.tabBarWrap}>
        <View style={s.tabBar}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const label = tab.id === 'haulbacks' ? t(listLanguage, 'tabs.haulbacks') : t(listLanguage, 'tabs.routeRoadblocks');
            return (
              <Pressable
                key={tab.id}
                style={[s.tab, isActive && s.tabActive]}
                onPress={() => setActiveTab(tab.id)}
                accessibilityRole="tab"
                accessibilityState={{ selected: isActive }}
              >
                <Text style={[s.tabLabel, isActive && s.tabLabelActive]}>
                  {label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* ── Tab Content ─────────────────────────────────────────────── */}
      <View style={s.content}>
          {activeTab === 'haulbacks'
            ? <HaulbacksTab listLanguage={listLanguage} />
            : <RouteRoadblocksTab listLanguage={listLanguage} />}
      </View>
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: AdminColors.bgPage,
  },

  headerTop: {
    paddingHorizontal: 32,
    paddingTop: 24,
    paddingBottom: 0,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },

  header: {
    paddingHorizontal: 32,
    paddingTop: 24,
    paddingBottom: 0,
  },

  // Breadcrumbs — design: .crumbs flex row gap 8 font-size 14 color #838383 margin-bottom 10
  crumbs: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
  },
  backText: {
    fontSize: 14,
    color: '#005CBC',
    fontFamily: FontFamily.regular,
  },
  crumbSep: {
    fontSize: 14,
    color: '#838383',
    fontFamily: FontFamily.regular,
  },
  crumbCur: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2D2D2D',
    fontFamily: FontFamily.medium,
  },

  // Page head — design: .page-head flex row align-center justify space-between margin-bottom 18
  pageHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  headerLeft: { flex: 1 },
  headerRight: { alignItems: 'flex-end', justifyContent: 'center' },

  // Page title — design: .page-title font-size 28 font-weight 700 line-height 40
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#2D2D2D',
    fontFamily: FontFamily.bold,
    lineHeight: 40,
  },

  // Tab bar — design: .tabs flex bg #fff border-radius 12 shadow elev-01 margin-bottom 20 overflow hidden
  tabBarWrap: {
    paddingHorizontal: 32,
    paddingBottom: 0,
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 20,
    ...Platform.select({
      ios: { shadowColor: 'rgba(0,94,192,1)', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 },
      android: { elevation: 2 },
    }),
  },

  // Tab — design: .tab flex:1 align-center justify-center gap 11 padding 13px 14px font-size 15 font-weight 600
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 13,
    paddingHorizontal: 14,
    borderRadius: 12,
    backgroundColor: 'transparent',
  },
  tabActive: {
    backgroundColor: '#14274E',
  },
  tabLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: '#2D2D2D',
    fontFamily: FontFamily.medium,
  },
  tabLabelActive: {
    color: '#fff',
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },

  // Content — design: the tab content sits directly in the page scroll; no card wrapper at this level
  content: {
    flex: 1,
    marginHorizontal: 32,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 24,
    ...Platform.select({
      ios: { shadowColor: 'rgba(0,94,192,1)', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 },
      android: { elevation: 2 },
    }),
  },
});

export default DeliveryRPMManagementScreen;
